import React, { useState } from 'react';
import CommonButton from '../../../Components/CommonButton';
import CommonLoader from '../../../Components/CommonLoader';
import CommonTable from '../../../Components/CommonTable';
import PageHeading from '../../../Components/PageHeading';
import StatusBadge from '../../../Components/StatusBadge';
import { Body, SubHeading } from '../../../Constants/Fonts';
import {
    risk_portal_ai_categorize,
    risk_portal_ai_duplicates,
    risk_portal_ai_risk_statement,
    risk_portal_ai_suggest_treatment,
} from '../../../services';

const unwrap = (res) =>
    res?.data?.response_data?.data ?? res?.data?.data ??
    res?.statusCode?.response_data?.data ?? res?.statusCode?.data ?? null;

const SAMPLE = 'Our main payment vendor could go bankrupt, disrupting payment processing for 3 weeks affecting 50,000 customers and exposing the business to significant revenue loss and customer trust damage.';

const FEATURES = [
    { title: 'Auto-Categorize', body: 'Classifies the risk into an ISO category (cyber, financial, operational, compliance…) with a confidence score.' },
    { title: 'Detect Duplicates', body: 'Finds semantically similar risks in your registry — catches paraphrased duplicates keyword matching misses.' },
    { title: 'Generate ISO Statement', body: 'Converts plain text into a formal ISO 31000 statement with threat, vulnerability, impact and suggested scores.' },
    { title: 'Suggest Treatment', body: 'Recommends a treatment strategy with specific controls, estimated effectiveness, cost and timeline.' },
];

// A label above a value block (used by the statement / treatment results).
const Field = ({ label, children, boxed = false }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Body as="span" variant={2} weight="semibold" color="var(--color-text-muted)" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Body>
        {boxed ? (
            <Body variant={1} color="var(--color-text-primary)" style={{ margin: 0, background: 'var(--color-surface-muted)', padding: 10, borderRadius: 'var(--radius-sm)', lineHeight: 1.6 }}>{children}</Body>
        ) : (
            <Body as="span" variant={2} color="var(--color-text-secondary)">{children || '—'}</Body>
        )}
    </div>
);

const similarityColor = (n) => (n > 70 ? 'var(--color-danger)' : n > 50 ? 'var(--color-high)' : 'var(--color-medium)');

// NovaAi — AI risk intelligence. Enter a risk description, then run one of four
// analyses (categorise / duplicates / ISO statement / treatment). Each response
// carries a `method` (nova_ai / jaccard / keyword_fallback) shown as a badge.
//
// Props: onOpenRisk(id) — open a risk from the duplicates table.
export default function NovaAi({ onOpenRisk }) {
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(null); // which action is running
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const hasInput = !!input.trim();

    const runAi = async (kind, call, label) => {
        setLoading(kind);
        setError(null);
        setResult(null);
        try {
            const res = await call();
            const d = unwrap(res);
            const innerErr = res?.statusCode?.error?.message || res?.errors?.message;
            if (innerErr) setError(innerErr);
            else if (d) setResult({ type: kind, ...d });
            else setError(`${label} returned an empty response. The AI service may be busy — try again in a moment.`);
        } catch (e) {
            setError(`${label} failed: ${e?.response?.data?.error?.message || e?.message || 'Service unavailable'}`);
        } finally {
            setLoading(null);
        }
    };

    const ACTIONS = [
        { kind: 'categorize', label: 'Auto-Categorize', run: () => risk_portal_ai_categorize({ description: input }) },
        { kind: 'duplicates', label: 'Detect Duplicates', run: () => risk_portal_ai_duplicates({ description: input }) },
        { kind: 'statement', label: 'Generate ISO Statement', run: () => risk_portal_ai_risk_statement({ description: input }) },
        { kind: 'treatment', label: 'Suggest Treatment', run: () => risk_portal_ai_suggest_treatment({ description: input, risk_type: '', severity: '' }) },
    ];

    const DUP_HEADERS = ['Risk ID', 'Description', 'Severity', 'Similarity', 'Reason'];
    const dupRows = (result?.duplicates || []).map((d) => ({
        data: [
            <Body as="span" variant={2} color="var(--color-primary)" weight="semibold">{d.risk_id || '—'}</Body>,
            <Body as="span" variant={2} color="var(--color-text-primary)">{d.risk || '—'}</Body>,
            <Body as="span" variant={2} color="var(--color-text-secondary)">{d.severity || '—'}</Body>,
            <Body as="span" variant={2} weight="bold" color={similarityColor(d.similarity)}>{d.similarity}%</Body>,
            <Body as="span" variant={2} color="var(--color-text-muted)">{d.reason || '—'}</Body>,
        ],
    }));

    return (
        <div className="rp1_nova">
            <PageHeading
                title="FORESEE AI: Risk Intelligence"
                subtitle="AI-powered risk analysis. Enter a risk description, then run any analysis below."
            />

            {/* Input */}
            <div className="rp1_widget">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                    <Body as="span" weight="semibold" color="var(--color-text-secondary)" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>Risk Description</Body>
                    {!hasInput && <CommonButton variant="secondary" text="Try sample text →" onClick={() => setInput(SAMPLE)} />}
                </div>
                <textarea
                    className="rp1_nova_textarea"
                    rows={4}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Describe a risk… e.g. 'Our main payment vendor could go bankrupt, disrupting processing for 3 weeks…'"
                />
                <div className="rp1_nova_actions">
                    {ACTIONS.map((a) => (
                        <CommonButton
                            key={a.kind}
                            text={loading === a.kind ? 'Analyzing…' : a.label}
                            onClick={() => runAi(a.kind, a.run, a.label)}
                            disable={!!loading || !hasInput}
                        />
                    ))}
                </div>
                {!hasInput && (
                    <Body variant={2} color="var(--color-text-muted)" style={{ margin: '8px 0 0' }}>
                        Type a risk description to enable the analysis buttons.
                    </Body>
                )}
            </div>

            {loading && (
                <div className="rp1_widget" style={{ position: 'relative', minHeight: 72 }}>
                    <CommonLoader overlay label="Calling FORESEE AI — typically 2–5 seconds…" />
                </div>
            )}

            {error && !loading && (
                <div className="rp1_widget rp1_nova_error">
                    <Body color="var(--color-danger)" style={{ margin: 0 }}>{error}</Body>
                    <CommonButton variant="secondary" text="Dismiss" onClick={() => setError(null)} />
                </div>
            )}

            {/* Result */}
            {result && !loading && (
                <div className="rp1_widget">
                    <div className="rp1_widget_head">
                        <Body as="span" weight="semibold" color="var(--color-text-secondary)" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>FORESEE AI Response</Body>
                        <CommonButton variant="secondary" text="Clear" onClick={() => setResult(null)} />
                    </div>

                    {result.type === 'categorize' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                                <StatusBadge variant="info" label={result.category} />
                                <StatusBadge variant="primary" label={`${Math.round((result.confidence || 0) * 100)}% confidence`} />
                                {result.method && <Body as="span" variant={2} color="var(--color-text-muted)">via {result.method}</Body>}
                            </div>
                            {result.reasoning && <Body variant={1} color="var(--color-text-secondary)" style={{ margin: 0 }}>{result.reasoning}</Body>}
                        </div>
                    )}

                    {result.type === 'duplicates' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <Body as="span" weight="semibold" color="var(--color-text-primary)">{result.count || 0} potential duplicate(s) found</Body>
                            {dupRows.length > 0 && (
                                <CommonTable
                                    headers={DUP_HEADERS}
                                    rows={dupRows}
                                    noPadding
                                    noScroll
                                    onRowClick={onOpenRisk ? (_row, i) => {
                                        const d = result.duplicates[i];
                                        if (d?.id != null) onOpenRisk(d.id);
                                    } : undefined}
                                />
                            )}
                        </div>
                    )}

                    {result.type === 'statement' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <Field label="Risk Statement" boxed>{result.risk_statement}</Field>
                            <div className="rp1_nova_grid2">
                                <Field label="Threat">{result.threat}</Field>
                                <Field label="Vulnerability">{result.vulnerability}</Field>
                            </div>
                            <Field label="Impact">{result.impact}</Field>
                            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                                {result.suggested_category && <StatusBadge variant="info" label={result.suggested_category} />}
                                <Body as="span" variant={2} color="var(--color-text-secondary)">Likelihood: <Body as="span" variant={2} weight="bold" color="var(--color-text-primary)">{result.suggested_likelihood}/5</Body></Body>
                                <Body as="span" variant={2} color="var(--color-text-secondary)">Impact: <Body as="span" variant={2} weight="bold" color="var(--color-text-primary)">{result.suggested_impact}/5</Body></Body>
                            </div>
                            {result.treatment_recommendation && <Field label="Treatment Recommendation">{result.treatment_recommendation}</Field>}
                        </div>
                    )}

                    {result.type === 'treatment' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                {result.recommended_strategy && <StatusBadge variant="success" label={result.recommended_strategy} />}
                                {result.cost_estimate && <StatusBadge variant="neutral" label={`Cost: ${result.cost_estimate}`} />}
                                {result.timeline && <StatusBadge variant="neutral" label={`Timeline: ${result.timeline}`} />}
                            </div>
                            <Field label="Treatment Plan" boxed>{result.treatment_plan}</Field>
                            {result.controls?.length > 0 && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    <Body as="span" variant={2} weight="semibold" color="var(--color-text-muted)" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>Suggested Controls</Body>
                                    <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        {result.controls.map((c, i) => <li key={i}><Body as="span" variant={2} color="var(--color-text-secondary)">{c}</Body></li>)}
                                    </ul>
                                </div>
                            )}
                            {result.estimated_effectiveness && (
                                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                    <Body as="span" variant={2} color="var(--color-text-secondary)">Effectiveness: <Body as="span" variant={2} weight="bold" color="var(--color-text-primary)">{result.estimated_effectiveness}%</Body></Body>
                                    {result.estimated_residual_likelihood && <Body as="span" variant={2} color="var(--color-text-secondary)">Residual L: <Body as="span" variant={2} weight="bold" color="var(--color-text-primary)">{result.estimated_residual_likelihood}</Body></Body>}
                                    {result.estimated_residual_impact && <Body as="span" variant={2} color="var(--color-text-secondary)">Residual I: <Body as="span" variant={2} weight="bold" color="var(--color-text-primary)">{result.estimated_residual_impact}</Body></Body>}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Feature descriptions */}
            <div className="rp1_nova_features">
                {FEATURES.map((f) => (
                    <div key={f.title} className="rp1_widget">
                        <SubHeading variant={3} as="span" color="var(--color-text-primary)" style={{ display: 'block', marginBottom: 6 }}>{f.title}</SubHeading>
                        <Body variant={2} color="var(--color-text-secondary)" style={{ margin: 0 }}>{f.body}</Body>
                    </div>
                ))}
            </div>
        </div>
    );
}
